import keyBy from "lodash/keyBy";
import { GetStaticPropsContext } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { useUser } from "@shared/auth";
import {
  AdCard,
  GenericAd,
  Ratio,
  QuickInfo,
  SidebarOverlay,
} from "@shared/components";
import { urls } from "@shared/constants";
import { Button, Container, Grid, Text } from "@shared/elements";
import {
  useAd,
  graphqlFetch,
  useGraphSWR,
  useGraphSWRImmutable,
} from "@shared/hooks";
import { VideoFile } from "@shared/types";

import {
  HomeCallQuery,
  HomeCallsQuery,
  HomeMySubmissionsForStatusQuery,
  TCallsFilterEntryStatus,
  TCallsFilterVisibilityStatus,
  type THomeCallQuery,
  type THomeCallsQuery,
  type THomeMySubmissionsForStatusQuery,
} from "__generated__/types";

import AppHeader from "src/components/AppHeader";
import CallCard from "src/components/CallCard";
import CallGridItem from "src/components/CallGridItem";
import DefaultOpenGraphTags from "src/components/DefaultOpenGraphTags";
import Footer from "src/components/Footer";
import NewsletterCard from "src/components/NewsletterCard";

import * as constants from "src/constants";
import { useCallCardLink, useQuickInfoClose } from "src/helpers";
import bundleMessages from "src/utils/bundleMessages";

export default function Home({ calls, closedCalls }: THomeCallsQuery) {
  const router = useRouter();
  const callCardLink = useCallCardLink();
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.index`);

  const user = useUser({
    redirectTo: constants.paths.login,
    returnAfterAuthentication: true,
  });

  const { data: callsData } = useGraphSWR<THomeCallsQuery>(HomeCallsQuery, {
    limit: 100,
    filter: {
      visibilityStatus: TCallsFilterVisibilityStatus.Private,
      entryStatus: TCallsFilterEntryStatus.OpenAndUpcoming,
    },
  });

  const privateCalls = callsData?.calls ?? [];

  const allCalls = useMemo(() => {
    if (privateCalls && privateCalls.length > 0) {
      return [...privateCalls, ...calls].sort(
        (a, b) =>
          new Date(a.submissionEndDate).getTime() -
          new Date(b.submissionEndDate).getTime(),
      );
    }
    return calls;
  }, [calls, privateCalls]);

  const { data: mySubmissionsData } =
    useGraphSWR<THomeMySubmissionsForStatusQuery>(
      HomeMySubmissionsForStatusQuery,
      {
        callIds: allCalls.map((call) => call.id),
      },
    );

  const mySubmissions = mySubmissionsData?.mySubmissions ?? [];

  const submissionsByCall = useMemo(() => {
    // create a map of submissions by call id
    if (mySubmissions) {
      return keyBy(mySubmissions, "call.id");
    }
    return {};
  }, [mySubmissions]);

  const gridAds = [useAd("Contest Grid Card"), useAd("Contest Grid Card", 1)];
  const { ad: bannerAd } = useAd("Contest Grid Banner");
  // If calls are greater than 10 then position will be 10 or last position
  // AND if calls are greater than 20 then 1 more will be at last position
  // If ad is available then index will be 1 lesser.
  const getNewsletterCardIndex = (totalCards: number, visibleAdCount = 0) =>
    totalCards > 10 ? 9 - visibleAdCount : totalCards;

  const visibleAdCount = gridAds
    .filter(Boolean)
    .filter((i) => i?.ad !== null).length;

  const [sliceIndexAfterAd, setSliceIndexAfterAd] = useState(
    getNewsletterCardIndex(calls.length, visibleAdCount),
  );

  useEffect(() => {
    setSliceIndexAfterAd(
      getNewsletterCardIndex(allCalls.length, visibleAdCount),
    );
  }, [visibleAdCount, allCalls.length]);

  const callId = router.query.quickInfo as string | undefined;
  const { call: quickInfoCall } = useGraphSWRImmutable<THomeCallQuery>(
    HomeCallQuery,
    { id: callId },
  );

  const onQuickInfoClose = useQuickInfoClose(quickInfoCall?.title?.en);

  type TCall = React.ComponentProps<typeof CallGridItem>["call"];
  const renderCallCard = (call: TCall) => (
    <CallGridItem
      key={call.id}
      call={call}
      resolveLink={callCardLink}
      submissionStatus={
        submissionsByCall ? submissionsByCall[call.id]?.status : "LOADING"
      }
    />
  );
  const renderClosedCallCard = (call: TCall) => (
    <CallGridItem key={call.id} call={call} resolveLink={callCardLink} />
  );

  return (
    <>
      <Head>
        <title key="title">Picter Contests</title>
        <DefaultOpenGraphTags />
      </Head>
      <AppHeader />
      <Container className="mt-14">
        {bannerAd && (
          <GenericAd
            key={bannerAd.id}
            campaign={bannerAd.campaign}
            type={bannerAd.content.mediaType}
            src={bannerAd.content.files.map(({ src }) => src)}
            impressionUrl={bannerAd.impressionUrl}
            trackingTags={bannerAd.trackingTags}
            clickUrl={bannerAd.clickUrl}
            poster={
              bannerAd.content.mediaType === "video"
                ? bannerAd.content.files.map(
                    (adFile) => (adFile as VideoFile).poster,
                  )
                : undefined
            }
            ratio={[Ratio.Square, Ratio.FourByOne]}
            className="mb-12"
          />
        )}
        <Text as="h2" weight="bold" size="3xl">
          {t("listHeaderLoggedin")}
        </Text>
        <Grid className="mt-12 sm:gap-y-14">
          {allCalls.slice(0, 1).map(renderCallCard)}
          {gridAds[0]?.ad && (
            <Grid.Item key={gridAds[0]?.ad.id}>
              <AdCard
                title={gridAds[0]?.ad.content.headline}
                campaign={gridAds[0]?.ad.campaign}
                type={gridAds[0]?.ad.content.mediaType}
                src={gridAds[0]?.ad.content.files[0].src}
                impressionUrl={gridAds[0]?.ad.impressionUrl}
                trackingTags={gridAds[0]?.ad.trackingTags}
                clickUrl={gridAds[0]?.ad.clickUrl}
                poster={
                  gridAds[0]?.ad.content.mediaType === "video"
                    ? (gridAds[0]?.ad.content.files[0] as VideoFile).poster
                    : undefined
                }
              />
            </Grid.Item>
          )}
          {allCalls.slice(1, 6).map(renderCallCard)}
          {gridAds[1]?.ad && (
            <Grid.Item key={gridAds[1]?.ad.id}>
              <AdCard
                title={gridAds[1]?.ad.content.headline}
                campaign={gridAds[1]?.ad.campaign}
                type={gridAds[1]?.ad.content.mediaType}
                src={gridAds[1]?.ad.content.files[0].src}
                impressionUrl={gridAds[1]?.ad.impressionUrl}
                trackingTags={gridAds[1]?.ad.trackingTags}
                clickUrl={gridAds[1]?.ad.clickUrl}
                poster={
                  gridAds[1]?.ad.content.mediaType === "video"
                    ? (gridAds[1]?.ad.content.files[0] as VideoFile).poster
                    : undefined
                }
              />
            </Grid.Item>
          )}
          {allCalls.slice(6, sliceIndexAfterAd).map(renderCallCard)}
          {user.isAuthenticated && !user.data?.newsletter && (
            <Grid.Item key="newsletter-card-1">
              <NewsletterCard trackingSourceName="call-grid:newsletter-card:grid" />
            </Grid.Item>
          )}
          {allCalls.slice(sliceIndexAfterAd).map(renderCallCard)}
          {allCalls.length > 20 &&
            user.isAuthenticated &&
            !user.data?.newsletter && (
              <Grid.Item key="newsletter-card-2">
                <NewsletterCard trackingSourceName="call-grid:newsletter-card:bottom" />
              </Grid.Item>
            )}
        </Grid>
      </Container>
      <Container className="mt-14">
        <Text
          as="h2"
          weight="bold"
          size="3xl"
          className="mt-10 pt-10 border-gray-300 border-t"
        >
          {t("archiveHeader")}
        </Text>
        <Grid className="mt-12 sm:gap-y-14">
          {closedCalls.map(renderClosedCallCard)}
        </Grid>
        <div className="mt-12 pb-14 flex justify-center">
          <Button
            variant="secondary"
            size="lg"
            as={Link}
            href={urls.archive(1)}
          >
            {t("archiveButton")}
          </Button>
        </div>
      </Container>
      <Footer />
      <SidebarOverlay
        show={Boolean(quickInfoCall)}
        size="wide"
        onClose={onQuickInfoClose}
      >
        {quickInfoCall && (
          <QuickInfo
            data={quickInfoCall}
            submissionStatus={submissionsByCall?.[quickInfoCall.id]?.status}
            showFooterLogo={false}
            utmMedium="call_grid"
          />
        )}
      </SidebarOverlay>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const { calls, closedCalls }: THomeCallsQuery = await graphqlFetch(
    JSON.stringify({
      query: HomeCallsQuery,
      variables: { limit: 100, closedCallsLimit: 12 },
    }),
  );
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.index`,
    ...AdCard.messages,
    ...AppHeader.messages,
    ...CallCard.messages,
    ...Footer.messages,
    ...GenericAd.messages,
    ...NewsletterCard.messages,
    ...SidebarOverlay.messages,
    ...QuickInfo.messages,
  ]);
  return {
    props: {
      calls,
      closedCalls,
      messages,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 60 second
    revalidate: 60, // In seconds
  };
}
